/**
 * Per-sale financial breakdown for Goals — matches mark-as-sold persistence.
 * BP spec: total sale = user + VL + QA gross; QA net = QA gross − rep − affiliates.
 */

export const REFERRAL_TYPE_DIRECT = 'Direct_Affiliate';
export const REFERRAL_TYPE_INDIRECT = 'Indirect_Affiliate';

export type BreakdownTotals = {
  totalSale: number;
  userPayout: number;
  vlFee: number;
  qaGross: number;
  salesRepCommission: number;
  directAffiliate: number;
  indirectAffiliate: number;
  qaNet: number;
  tickets: number;
};

export type SaleBreakdown = BreakdownTotals & {
  tpId: string;
  soldAt: string;
  eventName: string;
  userName: string;
  marketplace: string;
  seatLabel: string;
};

export type MonthlyBreakdownStats = BreakdownTotals & {
  month: number;
  revenue: number;
  profit: number;
};

export type TicketStatsResult = {
  monthly: MonthlyBreakdownStats[];
  annual: BreakdownTotals & { revenue: number; profit: number };
};

export const toFiniteNumber = (value: unknown): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

export const roundMoney = (value: number): number =>
  Math.round(value * 100) / 100;

export const emptyBreakdownTotals = (): BreakdownTotals => ({
  totalSale: 0,
  userPayout: 0,
  vlFee: 0,
  qaGross: 0,
  salesRepCommission: 0,
  directAffiliate: 0,
  indirectAffiliate: 0,
  qaNet: 0,
  tickets: 0,
});

export const addBreakdownTotals = (
  acc: BreakdownTotals,
  row: BreakdownTotals,
): BreakdownTotals => ({
  totalSale: roundMoney(acc.totalSale + row.totalSale),
  userPayout: roundMoney(acc.userPayout + row.userPayout),
  vlFee: roundMoney(acc.vlFee + row.vlFee),
  qaGross: roundMoney(acc.qaGross + row.qaGross),
  salesRepCommission: roundMoney(acc.salesRepCommission + row.salesRepCommission),
  directAffiliate: roundMoney(acc.directAffiliate + row.directAffiliate),
  indirectAffiliate: roundMoney(acc.indirectAffiliate + row.indirectAffiliate),
  qaNet: roundMoney(acc.qaNet + row.qaNet),
  tickets: acc.tickets + (row.tickets || 0),
});

const parseReferralData = (raw: unknown): Record<string, unknown> | null => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw as Record<string, unknown>;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  return null;
};

export const getReferralType = (ref: {
  referral_bonus?: { rb_type?: string | null } | null;
  referral_data?: unknown;
}): string => {
  const fromBonus = ref?.referral_bonus?.rb_type;
  if (fromBonus) return String(fromBonus);
  const parsed = parseReferralData(ref?.referral_data);
  if (parsed?.referralType) return String(parsed.referralType);
  return '';
};

/** QA gross profit — persisted tp_quick_cut_amount; fallback logitix − sold when missing. */
export const resolveQaGrossProfit = (ticket: {
  tp_quick_cut_amount?: number | string | null;
  tp_logitix_amount?: number | string | null;
  tp_sold_amount?: number | string | null;
}): number => {
  if (
    ticket.tp_quick_cut_amount != null &&
    String(ticket.tp_quick_cut_amount).trim() !== ''
  ) {
    return toFiniteNumber(ticket.tp_quick_cut_amount);
  }

  const logitixAmount = toFiniteNumber(ticket.tp_logitix_amount);
  const soldAmount = toFiniteNumber(ticket.tp_sold_amount);
  if (logitixAmount > 0 && soldAmount >= 0) {
    return Math.max(0, logitixAmount - soldAmount);
  }

  return 0;
};

export const breakdownSale = (placement: {
  tp_id?: string;
  tp_sold_at?: string | null;
  tp_logitix_amount?: number | string | null;
  tp_sold_amount?: number | string | null;
  tp_quick_cut_amount?: number | string | null;
  tp_marketplace_fee_percent?: number | string | null;
  tp_marketplace_name?: string | null;
  tp_execution_type?: string | null;
  tp_section?: string | null;
  tp_row?: string | null;
  tp_seat_no?: string | number | null;
  tp_referral_details?: Array<{
    referral_payout?: number | string | null;
    referral_percentage?: number | string | null;
    referral_data?: unknown;
    referral_bonus?: { rb_type?: string | null } | null;
  }> | null;
  user?: {
    u_full_name?: string | null;
    u_email_id?: string | null;
    u_direct_affiliate_id?: string | null;
    u_indirect_affiliate_id?: string | null;
    sales_reps?: {
      sr_commission_rate?: number | string | null;
    } | null;
  } | null;
  ticket?: {
    user?: {
      u_full_name?: string | null;
      u_email_id?: string | null;
      u_direct_affiliate_id?: string | null;
      u_indirect_affiliate_id?: string | null;
      sales_reps?: {
        sr_commission_rate?: number | string | null;
      } | null;
    } | null;
    event?: {
      e_name?: string | null;
    } | null;
  } | null;
}): SaleBreakdown => {
  const totalSale = toFiniteNumber(placement.tp_logitix_amount);
  const userPayout = toFiniteNumber(placement.tp_sold_amount);
  const qaGross = roundMoney(resolveQaGrossProfit(placement));
  // Residual VL so user + VL + QA gross equals total sale (mark-as-sold fields).
  const vlFee = roundMoney(Math.max(0, totalSale - userPayout - qaGross));

  let directAffiliate = 0;
  let indirectAffiliate = 0;
  for (const ref of placement.tp_referral_details || []) {
    const payout = toFiniteNumber(ref.referral_payout);
    if (payout <= 0) continue;
    const type = getReferralType(ref);
    if (type === REFERRAL_TYPE_INDIRECT) {
      indirectAffiliate += payout;
    } else {
      directAffiliate += payout;
    }
  }
  directAffiliate = roundMoney(directAffiliate);
  indirectAffiliate = roundMoney(indirectAffiliate);

  const placementUser = placement.ticket?.user || placement.user;
  const repRate = toFiniteNumber(placementUser?.sales_reps?.sr_commission_rate);

  // Apply commission capping based on affiliate presence
  const hasDirectAffiliate = Boolean(placementUser?.u_direct_affiliate_id);
  const hasIndirectAffiliate = Boolean(placementUser?.u_indirect_affiliate_id);

  let salesRepCommission = 0;
  if (hasIndirectAffiliate) {
  
    salesRepCommission = 0;
  } else if (hasDirectAffiliate) {
  
    const cappedRate = Math.min(repRate, 5);
    salesRepCommission = cappedRate > 0 && qaGross > 0 ? roundMoney(qaGross * cappedRate / 100) : 0;
  } else {
  
    salesRepCommission = repRate > 0 && qaGross > 0 ? roundMoney(qaGross * repRate / 100) : 0;
  }

  const qaNet = roundMoney(
    Math.max(0, qaGross - salesRepCommission - directAffiliate - indirectAffiliate),
  );

  const user = placementUser;
  const event = placement.ticket?.event;
  const seatParts = [
    placement.tp_section ? `Sec ${placement.tp_section}` : null,
    placement.tp_row ? `Row ${placement.tp_row}` : null,
    placement.tp_seat_no != null && String(placement.tp_seat_no).trim() !== ''
      ? `Seat ${placement.tp_seat_no}`
      : null,
  ].filter(Boolean);

  return {
    tpId: placement.tp_id || '',
    soldAt: placement.tp_sold_at || '',
    eventName: event?.e_name || '—',
    userName: user?.u_full_name || user?.u_email_id || '—',
    marketplace: placement.tp_marketplace_name || placement.tp_execution_type || '—',
    seatLabel: seatParts.join(' · ') || '—',
    totalSale: roundMoney(totalSale),
    userPayout: roundMoney(userPayout),
    vlFee,
    qaGross,
    salesRepCommission,
    directAffiliate,
    indirectAffiliate,
    qaNet,
    tickets: 1,
  };
};

export const buildTicketStatsFromPlacements = (
  placements: Parameters<typeof breakdownSale>[0][],
  year: number,
  dateRange?: { startDate: string; endDate: string },
): TicketStatsResult => {
  const monthly = Array.from({ length: 12 }, (_, index) => ({
    month: index + 1,
    ...emptyBreakdownTotals(),
    revenue: 0,
    profit: 0,
  }));

  for (const placement of placements) {
    if (!placement?.tp_sold_at) continue;
    const soldDate = new Date(placement.tp_sold_at);
    if (Number.isNaN(soldDate.getTime())) continue;
    if (soldDate.getFullYear() !== year) continue;
    if (dateRange?.startDate && dateRange?.endDate) {
      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      if (soldDate < start || soldDate > end) continue;
    }

    const monthIndex = soldDate.getMonth();
    if (monthIndex < 0 || monthIndex > 11) continue;

    const row = breakdownSale(placement);

    const bucket = addBreakdownTotals(monthly[monthIndex], row);
    monthly[monthIndex] = {
      ...bucket,
      month: monthIndex + 1,
      revenue: bucket.totalSale,
      profit: bucket.qaGross,
    };
  }

  const annual = monthly.reduce(
    (acc, month) => addBreakdownTotals(acc, month),
    emptyBreakdownTotals(),
  );

  return {
    monthly,
    annual: {
      ...annual,
      revenue: annual.totalSale,
      profit: annual.qaGross,
    },
  };
};

export const formatBreakdownCurrency = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);
